import React, { useState, useEffect } from 'react';
import './SettingsModal.css';
import {
  validateImageFile,
  compressImage,
  checkImageOrientation,
  saveBackgroundToStorage,
  clearBackgroundFromStorage,
  checkStorageAvailability,
  clearUploadedBackgrounds
} from '../utils/backgroundManager';

function SettingsModal({ isOpen, onClose, onBackgroundChange, currentBackground }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [warning, setWarning] = useState('');
  const [storageInfo, setStorageInfo] = useState(null);

  // Check storage when modal opens
  useEffect(() => {
    if (isOpen) {
      const info = checkStorageAvailability();
      setStorageInfo(info);
      
      if (!info.available) {
        setError('localStorage is not available. Check your browser privacy settings.');
      } else if (!info.hasSpace) {
        setWarning(`Storage almost full (${info.usedMB}MB used). Previous uploads will be replaced.`);
      }
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setError('');
    setWarning('');
    setUploading(true);

    try {
      // Check storage availability
      const storage = checkStorageAvailability();
      if (!storage.available) {
        setError('localStorage not available. Check browser privacy settings (try disabling Private Browsing on iOS).');
        setUploading(false);
        event.target.value = '';
        return;
      }

      // Validate file
      const validation = validateImageFile(file);
      if (!validation.valid) {
        setError(validation.message);
        setUploading(false);
        event.target.value = '';
        return;
      }

      // Check orientation
      const orientation = await checkImageOrientation(file);
      if (!orientation.isPortrait) {
        setWarning('Landscape image detected. Portrait orientation works best on mobile devices.');
      }

      // Clear previous uploads to free space (if storage is tight)
      if (storage.usedMB > 3) {
        console.log('Clearing previous uploads to free space...');
        clearUploadedBackgrounds();
      }

      // Compress and convert to base64
      console.log('Compressing image...');
      const compressedBase64 = await compressImage(file, 1080, 1920, 0.7); // Lower quality for iOS
      
      // Calculate final size
      const sizeKB = (compressedBase64.length * 0.75) / 1024;
      const sizeMB = sizeKB / 1024;
      console.log(`Compressed image size: ~${sizeKB.toFixed(0)}KB (${sizeMB.toFixed(2)}MB)`);

      // Check if compressed image will fit
      if (sizeMB > 4) {
        setError(`Compressed image too large (${sizeMB.toFixed(1)}MB). Try a smaller or simpler image.`);
        setUploading(false);
        event.target.value = '';
        return;
      }

      // Save to localStorage with error handling
      console.log('Saving to localStorage...');
      const saved = saveBackgroundToStorage(compressedBase64, 'upload');
      
      if (saved) {
        console.log('Upload successful!');
        onBackgroundChange({ type: 'upload', data: compressedBase64 });
        setTimeout(() => {
          onClose();
        }, 500);
      } else {
        // More specific error for iOS
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
        if (isIOS) {
          setError('Storage full or Private Browsing enabled. Try: 1) Use preset backgrounds, 2) Disable Private Browsing, or 3) Use a smaller image.');
        } else {
          setError('Failed to save. Storage may be full. Try a smaller image or use presets.');
        }
      }
    } catch (err) {
      console.error('Upload error:', err);
      
      // Detect iOS-specific errors
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      if (err.name === 'QuotaExceededError' || err.code === 22) {
        if (isIOS) {
          setError('iOS storage limit exceeded. Try disabling Private Browsing or use a preset background.');
        } else {
          setError('Storage quota exceeded. Try a smaller image or use preset backgrounds.');
        }
      } else {
        setError(`Upload failed: ${err.message}. Try a smaller or simpler image.`);
      }
    } finally {
      setUploading(false);
      // Reset file input
      event.target.value = '';
    }
  };

  const handlePresetSelect = (presetId) => {
    setError('');
    setWarning('');

    // Save preset to localStorage
    const saved = saveBackgroundToStorage(presetId, 'preset');
    
    if (saved) {
      onBackgroundChange({ type: 'preset', data: presetId });
      setTimeout(() => {
        onClose();
      }, 300);
    } else {
      setError('Failed to save preset selection.');
    }
  };

  const handleResetToDefault = () => {
    setError('');
    setWarning('');

    clearBackgroundFromStorage();
    onBackgroundChange({ type: 'default', data: null });
    
    setTimeout(() => {
      onClose();
    }, 300);
  };

  const handleOverlayClick = (e) => {
    if (e.target.className === 'settings-modal-overlay') {
      onClose();
    }
  };

  const isCurrentlySelected = (type, data) => {
    if (!currentBackground) return type === 'default';
    return currentBackground.type === type && currentBackground.data === data;
  };

  return (
    <div className="settings-modal-overlay" onClick={handleOverlayClick}>
      <div className="settings-modal">
        <div className="modal-header">
          <h2>Background Settings</h2>
          <button 
            className="close-button" 
            onClick={onClose}
            aria-label="Close settings"
          >
            ✕
          </button>
        </div>

        <div className="modal-content">
          {/* Upload Section */}
          <div className="upload-section">
            <h3>📁 Upload Your Image</h3>
            <label className="file-upload-label">
              <input
                type="file"
                accept="image/*"
                onChange={handleFileUpload}
                disabled={uploading || (storageInfo && !storageInfo.available)}
                className="file-input"
              />
              <span className="upload-button">
                {uploading ? 'Uploading...' : 'Choose File'}
              </span>
            </label>
            <p className="hint">Max 5MB • Portrait orientation recommended</p>
            
            {/* Storage Info for debugging */}
            {storageInfo && storageInfo.available && (
              <p className="storage-info">
                Storage: {storageInfo.usedMB}MB used / ~{storageInfo.estimatedLimitMB}MB limit
              </p>
            )}
          </div>

          {/* Divider */}
          <div className="divider">
            <span>OR</span>
          </div>

          {/* Preset Section */}
          <div className="preset-section">
            <h3>🖼️ Choose Preset</h3>
            <div className="preset-grid">
              {/* Default Cockpit */}
              <div 
                className={`preset-item ${isCurrentlySelected('default', null) ? 'selected' : ''}`}
                onClick={() => handlePresetSelect('default')}
              >
                <img 
                  src="/background.png" 
                  alt="Default Cockpit"
                  className="preset-thumbnail"
                />
                <span className="preset-label">Cockpit (Default)</span>
                {isCurrentlySelected('default', null) && (
                  <div className="selected-badge">✓</div>
                )}
              </div>

              {/* Preset 1 */}
              <div 
                className={`preset-item ${isCurrentlySelected('preset', '1') ? 'selected' : ''}`}
                onClick={() => handlePresetSelect('1')}
              >
                <img 
                  src="/presets/preset1.jpg" 
                  alt="Preset 1"
                  className="preset-thumbnail"
                />
                <span className="preset-label">Meditation Room</span>
                {isCurrentlySelected('preset', '1') && (
                  <div className="selected-badge">✓</div>
                )}
              </div>

              {/* Preset 2 */}
              <div 
                className={`preset-item ${isCurrentlySelected('preset', '2') ? 'selected' : ''}`}
                onClick={() => handlePresetSelect('2')}
              >
                <img 
                  src="/presets/preset2.jpg" 
                  alt="Preset 2"
                  className="preset-thumbnail"
                />
                <span className="preset-label">Yoga Space</span>
                {isCurrentlySelected('preset', '2') && (
                  <div className="selected-badge">✓</div>
                )}
              </div>

              {/* Preset 3 - NEW */}
              <div 
                className={`preset-item ${isCurrentlySelected('preset', '3') ? 'selected' : ''}`}
                onClick={() => handlePresetSelect('3')}
              >
                <img 
                  src="/presets/preset3.jpg" 
                  alt="Preset 3"
                  className="preset-thumbnail"
                />
                <span className="preset-label">Serene Nature</span>
                {isCurrentlySelected('preset', '3') && (
                  <div className="selected-badge">✓</div>
                )}
              </div>
            </div>
          </div>

          {/* Error/Warning Messages */}
          {error && (
            <div className="message error-message">
              ⚠️ {error}
            </div>
          )}
          {warning && (
            <div className="message warning-message">
              ℹ️ {warning}
            </div>
          )}
        </div>

        {/* Modal Actions */}
        <div className="modal-actions">
          <button 
            className="reset-button" 
            onClick={handleResetToDefault}
            disabled={uploading}
          >
            Reset to Default
          </button>
          <button 
            className="close-action-button" 
            onClick={onClose}
          >
            Close
          </button>
        </div>

        {/* Credits */}
        <div className="modal-credits">
          <a href="https://www.flaticon.com/free-icons/" title="flower icons" target="_blank" rel="noopener noreferrer">
            Icons created by Freepik - Flaticon
          </a>
        </div>
      </div>
    </div>
  );
}

export default SettingsModal;
